<template>
  <div class="problem-modal">
    <!-- Formulaire de signalement -->
    <div class="form-container">
      <!-- Sélection du type de problème -->
      <div class="form-field">
        <label for="problemType" class="form-label">Type de problème</label>
        <Dropdown
          id="problemType"
          v-model="selectedProblemType"
          :options="problemTypes"
          optionLabel="label"
          placeholder="Sélectionnez un type de problème"
          class="w-full"
          :class="{ 'p-invalid': submitted && !selectedProblemType }"
        />
        <small v-if="submitted && !selectedProblemType" class="p-error">Le type de problème est requis</small>
      </div>

      <!-- Checks - Avez-vous essayé de contacter... -->
      <div class="checks-container">
        <div class="check-item">
          <label>Client contacté ?</label>
          <div class="button-group">
            <Button :class="{
              'active': clientContacted === true,
              'yes-button': true,
              'yes-selected': clientContacted === true
            }" severity="success" text raised @click="clientContacted = true" icon="pi pi-check" label="Oui" />
            <Button :class="{
              'active': clientContacted === false,
              'no-button': true,
              'no-selected': clientContacted === false
            }" severity="danger" text raised @click="clientContacted = false" icon="pi pi-times" label="Non" />
          </div>
          <div v-if="clientContacted === false" class="contact-suggestion">
            <p>Veuillez essayer de contacter le client avant de signaler un problème</p>
            <Button severity="info" @click="callClient" icon="pi pi-phone" label="Appeler le client" class="p-button-sm" />
          </div>
        </div>
      </div>
  
      
      <!-- Photo du problème -->
      <div class="form-field">
        <ShopProgressiveImageUpload
          v-model="photo"
          :delivery-id="delivery.id"
          title="Photo du problème"
          description="Prenez une photo pour documenter le problème rencontré"
          :required="false"
        />
      </div>
      
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import ShopProgressiveImageUpload from './ShopProgressiveImageUpload.vue';
import axios from '@/api/axios';
import { useStore } from 'vuex'; // Import Vuex store

// Props
const props = defineProps({
  delivery: {
    type: Object,
    required: true
  }
});

// État du formulaire
const selectedProblemType = ref(null);
const comment = ref('');
const photo = ref('');
const submitted = ref(false);
const clientContacted = ref(null);
const isSubmitting = ref(false);

// Store Vuex
const store = useStore();
// Vérifier si le mode hors ligne est actif
const isOnline = computed(() => store.getters['sync/isOnline']);
const isUserOfflineMode = computed(() => store.getters.isUserOfflineMode);

// Types de problèmes disponibles
const problemTypes = [
  { code: 'CLOSED', label: 'Établissement fermé' },
  { code: 'REFUSED', label: 'Livraison refusée' },
  { code: 'INACCESSIBLE', label: 'Accès impossible' },
  { code: 'NO_CONTACT', label: 'Personne à contacter' },
  { code: 'OTHER', label: 'Autre raison' }
];

// Vérifier si le formulaire est valide
const isFormValid = computed(() => {
  return !!selectedProblemType && clientContacted !== null;
});

// Fonction pour appeler le client
const callClient = () => {
  const phoneNumber = props.delivery?.shop_delivery_address?.delivery_phone ||
                    props.delivery?.shop_delivery_address?.nearest_delivery_location?.telephone;
  
  if (phoneNumber) {
    const cleanPhone = phoneNumber.replace(/\s+/g, '');
    window.location.href = `tel:${cleanPhone}`;
  } else {
    alert("Numéro de téléphone non disponible");
  }
};

// Fonction pour gérer la confirmation
const handleConfirm = async () => {
  submitted.value = true;
  
  if (!isFormValid.value) {
    return { success: false, error: 'Formulaire incomplet' };
  }
  
  try {
    isSubmitting.value = true;
    
    // Préparer les données pour la mise à jour
    const updateData = {
      status: 'not_delivered',
      undone_reason: selectedProblemType.value.label
    };
    
    // Si une photo a été prise, l'inclure dans les données
    if (photo.value) {
      console.log('Photo détectée dans handleConfirm:', {
        photoId: photo.value,
        photoType: typeof photo.value
      });
      
      // Photo.value contient maintenant l'ID de l'image, pas le contenu base64
      updateData.photo = photo.value;
    }
    
    // Vérifier si en ligne ou hors ligne
    if (isOnline.value && !isUserOfflineMode.value) {
      // En ligne - Utiliser l'API directement
      const response = await axios.patch(
        `/livraison/shopDelivery/${props.delivery.id}/`, 
        updateData
      );
      
      // Préparer les données de retour avec la réponse du serveur
      const updatedDelivery = {
        ...props.delivery,
        ...response.data
      };
      
      return {
        success: true,
        updatedDelivery,
        undoneReason: selectedProblemType.value.label
      };
    } else {
      // Hors ligne ou mode hors ligne utilisateur
      console.log('Mode hors ligne détecté, stockage de la mise à jour shop_delivery:', {
        deliveryId: props.delivery.id,
        hasPhoto: !!photo.value,
        photoId: photo.value
      });
      
      // Utiliser le store pour gérer la synchronisation ultérieure
      await store.dispatch('sync/updateShopDelivery', {
        id: props.delivery.id,
        updates: updateData
      });
      
      // Créer une version mise à jour pour l'UI
      const updatedDelivery = {
        ...props.delivery,
        ...updateData
      };
      
      return {
        success: true,
        updatedDelivery,
        undoneReason: selectedProblemType.value.label,
        offlineMode: true
      };
    }
  } catch (error) {
    console.error('Erreur lors de la soumission du problème:', error);
    return { 
      success: false, 
      error: error.response?.data?.message || error.message || 'Erreur inconnue' 
    };
  } finally {
    isSubmitting.value = false;
  }
};

// Exposer l'état de validité et la fonction de confirmation
defineExpose({ isFormValid, handleConfirm });
</script>

<style scoped>
.problem-modal {
  width: 100%;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
}

.p-error {
  color: #EF4444;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

/* Styles pour les checks */
.checks-container {
  background-color: #f9fafb;
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
}

.check-item {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.check-item label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
}

.button-group {
  display: flex;
  gap: 0.5rem;
}

.button-group .p-button {
  flex: 1;
}

.yes-selected {
  background-color: rgba(16, 185, 129, 0.1) !important;
  color: #065f46 !important;
}

.no-selected {
  background-color: rgba(239, 68, 68, 0.1) !important;
  color: #991b1b !important;
}

.contact-suggestion {
  background-color: #f3f4f6;
  border-radius: 0.375rem;
  padding: 0.75rem;
  margin-top: 0.5rem;
}

.contact-suggestion p {
  font-size: 0.875rem;
  color: #4b5563;
  margin: 0 0 0.75rem 0;
}

@media (min-width: 640px) {
  .check-item {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
  }
  
  .check-item label {
    flex: 0 0 130px;
  }
  
  .button-group {
    flex: 1;
  }
  
  .contact-suggestion {
    margin-left: 130px;
    margin-top: 0.75rem;
  }
}
</style> 